import PageHeader from "../components/PageHeader";
import ConfigTabs from "../components/ConfigTabs";
import useSettings from "../hooks/useSettings";
import { Save, Image as ImageIcon } from "lucide-react";

const ASSETS = [
  { key: "logo_url", label: "Main Logo", desc: "Light background logo. Shown on top header.", ratio: "aspect-[4/1]", bg: "bg-white" },
  { key: "logo_dark_url", label: "Dark Logo", desc: "For dark backgrounds (sidebar, hero sections).", ratio: "aspect-[4/1]", bg: "bg-[#09090B]" },
  { key: "favicon_url", label: "Favicon", desc: "32x32 or 64x64 PNG/ICO shown in browser tab.", ratio: "aspect-square max-w-[80px]", bg: "bg-white" },
  { key: "login_logo_url", label: "Login Page Logo", desc: "Shown above the sign-in form.", ratio: "aspect-[4/1]", bg: "bg-white" },
  { key: "og_image_url", label: "Open Graph Image", desc: "1200x630 image used when links are shared on social media.", ratio: "aspect-[1200/630]", bg: "bg-gray-100" },
  { key: "loader_url", label: "Loader / Splash GIF", desc: "Animated loader shown on initial page load.", ratio: "aspect-square max-w-[120px]", bg: "bg-white" },
];

// Auth banners with overlay text controls
const AUTH_BANNERS = [
  {
    key: "auth_login_banner_url",
    label: "Login Page Banner",
    desc: "Hero image shown on /login & /forgot-password pages. Recommended ~1600×400.",
    kickerKey: "auth_login_kicker",
    headingKey: "auth_login_heading",
    kickerPlaceholder: "Maxlive88 — Welcome Back",
    headingPlaceholder: "Welcome Back\nto Maxlive88",
  },
  {
    key: "auth_register_banner_url",
    label: "Register Page Banner",
    desc: "Hero image shown on /register page. Recommended ~1600×400.",
    kickerKey: "auth_register_kicker",
    headingKey: "auth_register_heading",
    kickerPlaceholder: "Maxlive88 — Play Smart, Win Big",
    headingPlaceholder: "Create Your Account\nJoin Maxlive88 Today",
  },
];

export default function ConfigLogo() {
  const { form, set, save, loading } = useSettings();
  if (!form) {
    return (
      <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">
        Loading...
      </div>
    );
  }

  return (
    <div data-testid="config-logo-page">
      <PageHeader
        title="Configuration"
        subtitle="Branding assets · Logos, favicon, OG image, loader and auth-page banners (with overlay text)"
      />
      <ConfigTabs />

      <form
        onSubmit={(e) => { e.preventDefault(); save(); }}
        className="space-y-4 max-w-4xl"
        data-testid="config-logo-form"
      >
        {/* Standard branding assets */}
        {ASSETS.map((it) => (
          <div key={it.key} className="bg-white border border-gray-200 rounded-sm p-5">
            <div className="flex flex-col md:flex-row gap-5">
              <div className={`${it.ratio} ${it.bg} border border-gray-200 rounded-sm flex items-center justify-center overflow-hidden flex-shrink-0 w-full md:w-60`}>
                {form[it.key] ? (
                  <img
                    src={form[it.key]}
                    alt={it.label}
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <div className="text-gray-400 flex flex-col items-center gap-1">
                    <ImageIcon size={24} />
                    <span className="text-[10px] uppercase tracking-wider">No image</span>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-gray-900">{it.label}</div>
                <div className="text-xs text-gray-500 mb-2">{it.desc}</div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Image URL</label>
                <input
                  value={form[it.key] || ""}
                  onChange={(e) => set(it.key, e.target.value)}
                  className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm font-mono"
                  placeholder="https://..."
                  data-testid={`cfg-${it.key}`}
                />
              </div>
            </div>
          </div>
        ))}

        {/* Auth banners with overlay text */}
        {AUTH_BANNERS.map((it) => (
          <div key={it.key} className="bg-white border-2 border-emerald-200 rounded-sm p-5">
            <div className="text-sm font-semibold text-gray-900 mb-1">{it.label}</div>
            <div className="text-xs text-gray-500 mb-4">{it.desc}</div>

            <div className="flex flex-col md:flex-row gap-5 mb-4">
              <div className="aspect-[4/1] bg-[#0a1119] border border-gray-200 rounded-sm flex items-center justify-center overflow-hidden flex-shrink-0 w-full md:w-60 relative">
                {form[it.key] ? (
                  <img
                    src={form[it.key]}
                    alt={it.label}
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <div className="text-gray-500 flex flex-col items-center gap-1">
                    <ImageIcon size={24} />
                    <span className="text-[10px] uppercase tracking-wider">No image</span>
                  </div>
                )}
                {/* Overlay preview text */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#0a1119] via-[#0a1119cc] to-transparent" />
                <div className="absolute inset-0 px-3 flex flex-col justify-center">
                  <span className="text-[7px] font-bold text-emerald-400 uppercase tracking-widest truncate">
                    {form[it.kickerKey] || ""}
                  </span>
                  <span className="text-[10px] font-bold text-white leading-tight whitespace-pre-line line-clamp-2">
                    {form[it.headingKey] || ""}
                  </span>
                </div>
              </div>
              <div className="flex-1">
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Image URL</label>
                <input
                  value={form[it.key] || ""}
                  onChange={(e) => set(it.key, e.target.value)}
                  className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm font-mono"
                  placeholder="https://..."
                  data-testid={`cfg-${it.key}`}
                />
              </div>
            </div>

            {/* Overlay text controls */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-100">
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                  Kicker (small green tag above heading)
                </label>
                <input
                  value={form[it.kickerKey] || ""}
                  onChange={(e) => set(it.kickerKey, e.target.value)}
                  className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm"
                  placeholder={it.kickerPlaceholder || "Maxlive88 — Play Smart, Win Big"}
                  data-testid={`cfg-${it.kickerKey}`}
                />
                <p className="text-[11px] text-gray-500 mt-1">Leave empty to hide the green tag.</p>
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                  Heading (big white text)
                </label>
                <textarea
                  value={form[it.headingKey] || ""}
                  onChange={(e) => set(it.headingKey, e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm resize-y"
                  placeholder={it.headingPlaceholder || "Hello\nWelcome to Maxlive88"}
                  data-testid={`cfg-${it.headingKey}`}
                />
                <p className="text-[11px] text-gray-500 mt-1">Press Enter for a new line. Leave empty to hide.</p>
              </div>
            </div>
          </div>
        ))}

        <div className="flex justify-end pt-2">
          <button
            type="submit"
            disabled={loading}
            className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="cfg-logo-save"
          >
            <Save size={14} /> {loading ? "Saving..." : "Save Logo & Banners"}
          </button>
        </div>
      </form>
    </div>
  );
}
